import { useState, FormEvent } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'

const NONE = '__none__'

// ─────────────────── Types ───────────────────
type Engagement = { id: number; code: string; name: string }

type Project = {
  id: number; code: string; engagement: number; engagement_code: string; engagement_name: string
  account_name: string | null; name: string; description: string
  billing_type: 'T_AND_M' | 'FIXED_FEE' | 'MILESTONE' | 'RETAINER' | 'NON_BILLABLE'
  budget_amount: string; budget_hours: string; nte_amount: string | null
  start_date: string | null; end_date: string | null
  manager: number | null; manager_name: string | null
  currency_code: string
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'DELIVERED' | 'CLOSED' | 'CANCELLED'
  notes: string
  phase_count: number; task_count: number; deliverable_count: number; milestone_count: number
}

type CreateInput = {
  engagement: number | ''; name: string; description: string
  billing_type: Project['billing_type']; budget_amount: string; budget_hours: string
  nte_amount: string; start_date: string; end_date: string
  manager_person: number | null
  currency_code: string; status: Project['status']; notes: string
}

const emptyCreate: CreateInput = {
  engagement: '', name: '', description: '', billing_type: 'T_AND_M',
  budget_amount: '0', budget_hours: '0', nte_amount: '',
  start_date: '', end_date: '', manager_person: null,
  currency_code: 'EUR', status: 'DRAFT', notes: '',
}

const STATUS_COLOR: Record<Project['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const BILLING_LABEL: Record<Project['billing_type'], string> = {
  T_AND_M: 'T&M', FIXED_FEE: 'Fixed', MILESTONE: 'Milestone',
  RETAINER: 'Retainer', NON_BILLABLE: 'Non-billable',
}

// ─────────────────── Page ───────────────────
export default function ConsultingProjects() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const engagementFilter = searchParams.get('engagement')

  const projectsQ = useQuery({
    queryKey: ['consulting', 'projects', engagementFilter],
    queryFn: async () => {
      const url = engagementFilter ? `/consulting/projects/?engagement=${engagementFilter}` : '/consulting/projects/'
      return (await api.get<Project[]>(url)).data
    },
  })
  const engagementsQ = useQuery({
    queryKey: ['consulting', 'engagements'],
    queryFn: async () => (await api.get<Engagement[]>('/consulting/engagements/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<{ id: number; full_name: string }[]>('/hr/persons/')).data,
  })
  const meQ = useQuery({
    queryKey: ['me'],
    queryFn: async () => (await api.get('/auth/me/')).data,
    staleTime: 5 * 60 * 1000,
  })

  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (p: CreateInput) => (await api.post<Project>('/consulting/projects/', {
      ...p, engagement: Number(p.engagement),
      nte_amount: p.nte_amount === '' ? null : p.nte_amount,
      start_date: p.start_date === '' ? null : p.start_date,
      end_date: p.end_date === '' ? null : p.end_date,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['consulting', 'projects'] })
      setCreating(false)
      // After create, take user straight into the new project's detail page.
      navigate(`/admin/consulting/projects/${created.id}`)
    },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/projects/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'projects'] }),
  })

  const projects = projectsQ.data || []
  const engagements = engagementsQ.data || []
  const persons = personsQ.data || []

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Projects</h2>
          <p className="text-sm text-gray-500">
            {projects.length} total{engagementFilter && ' (filtered by engagement)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {engagementFilter && (
            <button onClick={() => setSearchParams({})}
              className="text-sm text-gray-600 hover:underline">Clear filter</button>
          )}
          <Button onClick={() => setCreating(true)} disabled={engagements.length === 0}>
            + New project
          </Button>
        </div>
      </div>

      {projectsQ.isLoading ? <p className="text-gray-500">Loading…</p> : projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No projects yet. Create an engagement first, then add projects under it.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Engagement</Th>
                <Th>Billing</Th>
                <Th>Manager</Th>
                <Th className="text-right">Budget</Th>
                <Th className="text-right">Hrs</Th>
                <Th className="text-right">WBS</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-blue-50 cursor-pointer"
                  onClick={() => navigate(`/admin/consulting/projects/${p.id}`)}
                >
                  <Td className="font-mono text-xs">
                    <Link
                      to={`/admin/consulting/projects/${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:underline"
                    >
                      {p.code}
                    </Link>
                  </Td>
                  <Td className="font-medium">{p.name}</Td>
                  <Td className="text-xs">
                    <div className="font-mono">{p.engagement_code}</div>
                    <div className="text-gray-500">{p.account_name}</div>
                  </Td>
                  <Td><span className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{BILLING_LABEL[p.billing_type]}</span></Td>
                  <Td className="text-xs">{p.manager_name || '—'}</Td>
                  <Td className="text-right font-mono">{p.currency_code} {Number(p.budget_amount).toFixed(2)}</Td>
                  <Td className="text-right font-mono text-xs">{Number(p.budget_hours).toFixed(0)}h</Td>
                  <Td className="text-right text-xs text-gray-500">
                    {p.phase_count}p · {p.task_count}t · {p.deliverable_count}d · {p.milestone_count}m
                  </Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[p.status]}`}>{p.status}</span></Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/admin/consulting/projects/${p.id}`}
                      className="text-blue-600 hover:underline mr-3"
                    >
                      Open
                    </Link>
                    <button onClick={() => { if (confirm(`Delete ${p.code}?`)) deleteMut.mutate(p.id) }}
                      className="text-red-600 hover:underline">Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateProjectDialog
          initial={{
            ...emptyCreate,
            engagement: engagementFilter ? Number(engagementFilter) : '',
            // Default the project manager to the current user's Employee record
            // (so a Manager creating a project doesn't lose visibility of it
            // under the hierarchy scoping). User can change it in the picker.
            manager_person: meQ.data?.person_id ?? null,
          }}
          engagements={engagements}
          persons={persons}
          onCancel={() => setCreating(false)}
          onSave={(payload) => createMut.mutate(payload)}
          saving={createMut.isPending}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }: { children: React.ReactNode; className?: string; onClick?: (e: any) => void }) {
  return <td onClick={onClick} className={`px-4 py-2 text-gray-700 ${className}`}>{children}</td>
}

// ─────────────────── Create-only dialog ───────────────────
// Edit happens on the dedicated /admin/consulting/projects/:id page now.
function CreateProjectDialog({ initial, engagements, persons, onCancel, onSave, saving }: {
  initial: CreateInput; engagements: Engagement[]
  persons: { id: number; full_name: string }[]
  onCancel: () => void; onSave: (p: CreateInput) => void; saving: boolean
}) {
  const [form, setForm] = useState<CreateInput>(initial)
  function submit(e: FormEvent) { e.preventDefault(); onSave(form) }
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <h3 className="text-lg font-bold">New project</h3>
        <p className="text-xs text-gray-500">
          A bare-minimum row to get started. You'll land on the project page where you can fill in budget, dates,
          team and the full WBS.
        </p>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="cp-name">Project name</FieldLabel>
            <Input id="cp-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="cp-engagement">Engagement</FieldLabel>
              <SearchableSelect
                id="cp-engagement"
                value={form.engagement ? String(form.engagement) : undefined}
                onChange={(v) => setForm({ ...form, engagement: Number(v) })}
                placeholder="— select —"
                options={engagements.map((eng) => ({
                  value: String(eng.id), label: eng.name, hint: eng.code,
                }))}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="cp-billing-type">Billing type</FieldLabel>
              <Select
                value={form.billing_type}
                onValueChange={(v) => setForm({ ...form, billing_type: v as Project['billing_type'] })}
              >
                <SelectTrigger id="cp-billing-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="T_AND_M">Time &amp; Materials</SelectItem>
                  <SelectItem value="FIXED_FEE">Fixed Fee</SelectItem>
                  <SelectItem value="MILESTONE">Milestone</SelectItem>
                  <SelectItem value="RETAINER">Retainer</SelectItem>
                  <SelectItem value="NON_BILLABLE">Non-billable</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="cp-currency">Currency</FieldLabel>
              <Input id="cp-currency" value={form.currency_code}
                onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })}
                maxLength={3} className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="cp-manager">Manager</FieldLabel>
              <SearchableSelect
                id="cp-manager"
                value={form.manager_person ? String(form.manager_person) : NONE}
                onChange={(v) => setForm({ ...form, manager_person: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
                ]}
              />
            </Field>
          </div>
        </FieldGroup>

        <div className="flex justify-end space-x-2 pt-2 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={saving || !form.name || !form.engagement}>
            {saving ? 'Creating…' : 'Create & open'}
          </Button>
        </div>
      </form>
    </div>
  )
}
